import { stripPreviewItemsFromBlock } from '@/lib/blocks/collection/data-widget/blocks';
import { type DefaultBlockInput, normalizeDefaultBlocks } from '@/lib/blocks/default-blocks';
import { normalizeBlocksForEditor } from '@/lib/blocks/tree';
import { cmsPagesEditorPath, cmsPagesPreviewPath } from '@/lib/routes';
import type { ResolvedSectionPropsResult } from '@/lib/section-registry';
import { resolveSectionProps } from '@/lib/section-registry';
import { getDocument, parseUrl } from '@/lib/utils';
import type { Page, PageSlots, PageVersion, RawPageFromApi, SectionStructure } from '@/types/page';
import { PageType } from '@/types/page';
import type { PageSection } from '@/types/page-section';
import type { SectionTemplateItem } from '@/types/section-template';

export const PREVIEW_NAV_MESSAGE = 'cms-preview-nav';

export function getPublicPagePermalink(page: { path?: string; pageType?: string }): string {
  if (page.pageType === PageType.HOME) {
    return '';
  }

  const path = page.path?.trim();
  if (!path) {
    return '';
  }

  return path.startsWith('/') ? path : `/${path}`;
}

export function getPublicPageUrl(
  siteDomain: string | undefined,
  page: { path?: string; pageType?: string },
): string | null {
  const domain = siteDomain?.trim();
  if (!domain) {
    return null;
  }

  return `https://${domain}${getPublicPagePermalink(page)}`;
}

export interface PublicNavMode {
  isEditor: boolean;
  isPreview: boolean;
}

export function getPublicNavPageUrl(
  page: { id: string; path?: string; pageType?: string },
  mode: PublicNavMode,
): string {
  if (mode.isEditor) {
    return cmsPagesEditorPath(page.id);
  }

  if (mode.isPreview) {
    const currentUrl = getDocument()?.location.href ?? '';
    const { searchParams } = parseUrl(currentUrl);
    const currentParams = searchParams.toString();
    return cmsPagesPreviewPath({ id: page.id }) + (currentParams ? `?${currentParams}` : '');
  }

  return getPublicPagePermalink(page) || '/';
}

export function isPublicNavPageActive(
  page: RawPageFromApi,
  currentUrl: string,
  getPageUrl: (page: RawPageFromApi) => string,
): boolean {
  const pageUrl = getPageUrl(page);
  return currentUrl === pageUrl || currentUrl.startsWith(`${pageUrl}/`);
}

/** Editor render / DnD key (`uiKey ?? id`). Not the persisted cross-version `stableId`. */
export const getSectionUiKey = (section: Pick<PageSection, 'id' | 'uiKey'>): string =>
  section.uiKey ?? section.id;

export const getMainSections = (page: Page | null): PageSection[] => {
  return page?.slots?.main.sections ?? [];
};

const normalizeSection = (section: PageSection): PageSection => {
  const normalizedBlocks = normalizeBlocksForEditor(section.blocks);
  if (section.uiKey) {
    // Preserve existing uiKey so server ID remaps do not change render identity.
    if (normalizedBlocks === section.blocks) {
      return section;
    }

    return {
      ...section,
      blocks: normalizedBlocks,
    };
  }

  return {
    ...section,
    blocks: normalizedBlocks,
    uiKey: getSectionUiKey(section),
  };
};

export const updateMainSections = (page: Page, sections: PageSection[]): Page => {
  const currentSlots = page.slots ?? { main: { visible: true, sections: [] } };
  const normalizedSections = sections.map(normalizeSection);
  return {
    ...page,
    slots: {
      ...currentSlots,
      main: {
        ...currentSlots.main,
        sections: normalizedSections,
      },
    } as PageSlots,
  };
};

export const normalizePage = (page: Page): Page => {
  const sections = getMainSections(page);

  return updateMainSections(
    {
      ...page,
      url: page.url ?? cmsPagesEditorPath(page.id),
    },
    sections,
  );
};

export const remapSectionsAndBlockIds = ({
  sections,
  sectionsMapping,
  blocksMapping,
}: {
  sections: PageSection[];
  sectionsMapping: Record<string, string>;
  blocksMapping: Record<string, string>;
}) => {
  let hasRemappedIds = false;

  const remappedSections = sections.map((section) => {
    const mappedSectionId = sectionsMapping[section.id] ?? section.id;
    const remappedBlocks = (section.blocks || []).map((block) => {
      const mappedBlockId = blocksMapping[block.id] ?? block.id;
      const mappedParentBlockId = block.parentBlockId
        ? (blocksMapping[block.parentBlockId] ?? block.parentBlockId)
        : block.parentBlockId;

      if (mappedBlockId !== block.id || mappedParentBlockId !== block.parentBlockId) {
        hasRemappedIds = true;
        return {
          ...block,
          id: mappedBlockId,
          parentBlockId: mappedParentBlockId,
        };
      }

      return block;
    });

    if (mappedSectionId !== section.id) {
      hasRemappedIds = true;
      return {
        ...section,
        id: mappedSectionId,
        // Preserve the React remount key so autosave id remaps do not remount editors.
        uiKey: getSectionUiKey(section),
        blocks: remappedBlocks,
      };
    }

    if (remappedBlocks.some((block, index) => block !== (section.blocks || [])[index])) {
      return {
        ...section,
        blocks: remappedBlocks,
      };
    }

    return section;
  });

  return {
    hasRemappedIds,
    remappedSections,
  };
};

export const remapPageContentIds = ({
  page,
  sectionsMapping,
  blocksMapping = {},
}: {
  page: Page;
  sectionsMapping: Record<string, string>;
  blocksMapping?: Record<string, string>;
}): { hasRemappedIds: boolean; page: Page } => {
  const sections = getMainSections(page);
  if (sections.length === 0) {
    return { hasRemappedIds: false, page };
  }

  const { hasRemappedIds, remappedSections } = remapSectionsAndBlockIds({
    sections,
    sectionsMapping,
    blocksMapping,
  });

  return {
    hasRemappedIds,
    page: hasRemappedIds ? updateMainSections(page, remappedSections) : page,
  };
};

export const buildPreviewNavigation = (
  page: Page,
): Pick<Page, 'id' | 'title' | 'navigationTitle'> => ({
  id: page.id,
  title: page.title,
  navigationTitle: page.navigationTitle ?? undefined,
});

export const getSortedSections = (sections: PageSection[]): PageSection[] =>
  [...sections].sort((a, b) => a.rowPosition - b.rowPosition);

export function resolveInsertRowPosition({
  insertRowPosition,
  sectionType,
  pageSections,
}: {
  insertRowPosition?: number;
  sectionType: string;
  pageSections: Pick<PageSection, 'sectionType' | 'rowPosition'>[];
}): number {
  if (typeof insertRowPosition === 'number') {
    return insertRowPosition;
  }

  if (sectionType === 'page_title') {
    return 0;
  }

  if (sectionType === 'hero') {
    const sortedSections = [...pageSections].sort((a, b) => a.rowPosition - b.rowPosition);
    return sortedSections[0]?.sectionType === 'page_title' ? 1 : 0;
  }

  return pageSections.length;
}

export const shouldSyncPreviewNavigation = (
  previewNavigationPage: Pick<Page, 'id' | 'title' | 'navigationTitle'> | null,
  pageId: string,
): boolean => {
  return !previewNavigationPage || previewNavigationPage.id === pageId;
};

export async function resolveSectionPropsForInsert(
  sectionTemplateCode: string,
  props: Record<string, unknown>,
  context?: { pageId?: string },
): Promise<ResolvedSectionPropsResult> {
  return resolveSectionProps(sectionTemplateCode, props, context);
}

export function getSectionTemplateInsertDefaults(
  template: Pick<SectionTemplateItem, 'defaultProps' | 'defaultBlocks'> | null | undefined,
) {
  const props = template?.defaultProps ?? {};
  const blocks =
    template && Array.isArray(template.defaultBlocks) && template.defaultBlocks.length > 0
      ? normalizeDefaultBlocks(template.defaultBlocks)
      : undefined;
  return { props, blocks };
}

export async function buildSectionsData(
  sectionsStructure: SectionStructure[],
  templatesByCode: Map<string, SectionTemplateItem>,
): Promise<Array<SectionStructure & { rowPosition: number; visible: boolean }>> {
  return Promise.all(
    sectionsStructure.map(async (section, index) => {
      const templateMeta = templatesByCode.get(section.sectionTemplateCode);
      const variantValue = section.variant ?? templateMeta?.variants[0]?.value ?? 'default';
      const templateProps = section.props || {};
      const { props: defaultProps, blocks: defaultBlocks } =
        getSectionTemplateInsertDefaults(templateMeta);

      let mergedProps = { ...defaultProps, ...templateProps };

      if (section.sectionTemplateCode) {
        try {
          const resolved = await resolveSectionPropsForInsert(
            section.sectionTemplateCode,
            mergedProps,
          );
          mergedProps = resolved.props;
        } catch (error) {
          console.error(
            `Failed to resolve props for section template ${section.sectionTemplateCode}:`,
            error,
          );
        }
      }

      const sectionBlocks =
        Array.isArray(section.blocks) && section.blocks.length > 0
          ? normalizeDefaultBlocks(section.blocks as DefaultBlockInput[])
          : undefined;
      const rawBlocks = sectionBlocks ?? defaultBlocks;

      return {
        sectionTemplateCode: section.sectionTemplateCode,
        variant: variantValue,
        props: mergedProps,
        ...(section.contentPayload ? { contentPayload: section.contentPayload } : {}),
        ...(section.blockOverrides ? { blockOverrides: section.blockOverrides } : {}),
        ...(rawBlocks ? { blocks: rawBlocks.map(stripPreviewItemsFromBlock) } : {}),
        rowPosition: index,
        visible: true,
        ...(section.permissions && { permissions: section.permissions }),
      };
    }),
  );
}

export function getPageDisplayTitle(page: Pick<Page, 'title' | 'navigationTitle'>): string {
  return page.navigationTitle || page.title;
}

// getPublicPagePermalink returns '' for the home page, which is not a usable path.
export function getPageDisplayPath(page: Pick<Page, 'path' | 'pageType'>): string {
  return getPublicPagePermalink(page) || '/';
}

export const getVersionBadge = (
  version: PageVersion,
): { text: string; colorScheme: 'secondary' | 'primary' | 'success' } | null => {
  if (version.isLive) {
    return { text: 'Last Published', colorScheme: 'success' };
  }
  return null;
};
